import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Components
import { TopRibbon } from './components/TopRibbon/TopRibbon';
import { Sidebar } from './components/Sidebar/Sidebar';
import { Login } from './components/Auth/Login';
import { Register } from './components/Auth/Register';
import { Dashboard } from './components/Dashboard/Dashboard';
import { ContentEditor } from './components/ContentEditor/ContentEditor';
import { History } from './components/History/History';
import { Settings } from './components/Common/Settings';
import { SessionWatchdog } from './components/Common/SessionWatchdog';
import { FeatureGate } from './components/Common/FeatureGate';
import { AdminPanel } from './components/Admin/AdminPanel';

// Content Pages
import { VideoToScript } from './pages/VideoToScript';
import { VideoToVideo }  from './pages/VideoToVideo';
import { QuickAI } from './pages/QuickAI';
import { MediaAI } from './pages/MediaAI';
import { NewsAnchorFormats } from './pages/NewsAnchorFormats';
import { BreakingPlates } from './pages/BreakingPlates';
import { BreakingPoints } from './pages/BreakingPoints';
import { Tops } from './pages/Tops';
import { NewsBulletin } from './pages/NewsBulletin';
import { HeadlinesSlugs } from './pages/HeadlinesSlugs';
import { DigitalAnchorVisual } from './pages/DigitalAnchorVisual';
import { BreakingNewsPresentation } from './pages/BreakingNewsPresentation';
import { Scrollings } from './pages/Scrollings';
import { AnchorBgTitles } from './pages/AnchorBgTitles';
import { VoiceOver } from './pages/VoiceOver';
import { SpeedNews } from './pages/SpeedNews';
import { ScriptModes } from './pages/ScriptModes';
import { Indepth30 } from './pages/Indepth30';
import { OffTheRecord30 } from './pages/OffTheRecord30';
import { SpecialFocus30 } from './pages/SpecialFocus30';
import { Analysis10 } from './pages/Analysis10';
import { Indepth } from './pages/Indepth';
import { OffTheRecord } from './pages/OffTheRecord';
import { SpecialFocus } from './pages/SpecialFocus';
import { Analysis } from './pages/Analysis';
import { SocialMedia } from './pages/SocialMedia';
import { PromoScript } from './pages/PromoScript';
import { CommercialInterviews } from './pages/CommercialInterviews';
import { Sports } from './pages/Sports';
import { ConspiracyTheory } from './pages/ConspiracyTheory';
import { BurningIssue } from './pages/BurningIssue';
import { CrimeStory } from './pages/CrimeStory';
import { TCR } from './pages/TCR';
import { WorldNews } from './pages/WorldNews';
import { Automation } from './pages/Automation';
import { FullStoryList } from './pages/FullStory/FullStoryList';
import { FullStoryEditor } from './pages/FullStory/FullStoryEditor';
import { FullStoryVideoList } from './pages/FullStoryVideo/FullStoryVideoList';
import { FullStoryVideoEditor } from './pages/FullStoryVideo/FullStoryVideoEditor';
import { PlateTemplates } from './pages/FullStoryVideo/PlateTemplates';

// Voice & Dubbing
import { ScriptToVoice } from './pages/ScriptToVoice';
import { VoiceArtistManager } from './pages/Admin/VoiceArtistManager';
import { DubbingHistoryAdmin } from './pages/Admin/DubbingHistoryAdmin';
import { LanguageChange } from './pages/LanguageChange';

// Settings Pages
import { AIProviderSettings } from './pages/Settings/AIProviderSettings';
import { PromptEditor } from './pages/Settings/PromptEditor';
import { ProfileSettings } from './pages/Settings/ProfileSettings';
import { EditorialRules } from './pages/Settings/EditorialRules';
import { UsageBilling } from './pages/Settings/UsageBilling';
import { UserManagement } from './pages/Settings/UserManagement';
import { IpWhitelistSettings } from './pages/Settings/IpWhitelistSettings';
import { MediaAIProviders } from './pages/Settings/MediaAIProviders';
import { AutomationSources } from './pages/Settings/AutomationSources';

// Store
import { useAuthStore, useThemeStore } from './utils/store';

// Icons
import { FiShield } from 'react-icons/fi';

// Styles
import './styles/globals.css';

const ProtectedRoute = ({ children, adminOnly, allowManager }) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const managerAllowed = allowManager && user?.role === 'manager';
  if (adminOnly && !isAdmin && !managerAllowed) return <Navigate to="/dashboard" replace />;
  return children;
};

// App shell — responsive [Sidebar | Main content] layout.
//   Mobile  (< 768px) : Sidebar renders as an overlay drawer (fixed, off-canvas)
//                        so it takes no layout space — main content is full width.
//   Tablet  (768–1023px): Sidebar occupies a fixed icon rail; main content is
//                        offset via the `.sidebar-offset` utility (--sidebar-width-collapsed).
//   Desktop (>= 1024px) : Sidebar occupies its full fixed width; main content is
//                        offset via `.sidebar-offset` (--sidebar-width).
// Content itself is capped at max-w-7xl, and additionally widened (but still
// centered) up to max-w-screen-2xl at the 3xl (2000px+) ultra-wide breakpoint
// so lines of text don't stretch edge-to-edge on very large monitors.
const AppLayout = ({ children }) => (
  <div className="flex flex-col min-h-screen">
    <TopRibbon />
    <div className="flex flex-1 pt-16">
      <Sidebar />
      <main className="flex-1 sidebar-offset overflow-auto">
        <div className="p-3 sm:p-4 md:p-6 max-w-7xl 3xl:max-w-screen-2xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  </div>
);

const P = ({ children, admin, allowManager }) => (
  <ProtectedRoute adminOnly={admin} allowManager={allowManager}>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

function App() {
  const { initAuth, isAuthenticated } = useAuthStore();
  const theme = useThemeStore((s) => s.theme);
  const [ipBlockedMsg, setIpBlockedMsg] = React.useState(null);

  useEffect(() => {
    initAuth();
  }, []);

  // Listen for IP whitelist block events fired by the API interceptor
  useEffect(() => {
    const handler = (e) => setIpBlockedMsg(e.detail?.message || 'Access restricted by IP address.');
    window.addEventListener('ip-blocked', handler);
    return () => window.removeEventListener('ip-blocked', handler);
  }, []);

  // Full-screen block when the server rejects the user's IP address
  if (ipBlockedMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
            <FiShield size={28} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Access Restricted</h1>
          <p className="text-sm text-gray-600 leading-relaxed">{ipBlockedMsg}</p>
          <p className="text-xs text-gray-400">
            If you believe this is an error, contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <SessionWatchdog />
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Dashboard & History */}
        <Route path="/dashboard" element={<P><Dashboard /></P>} />
        <Route path="/history" element={<P><History /></P>} />

        {/* Automation */}
        <Route path="/automation" element={<P><Automation /></P>} />
        <Route path="/full-story" element={<P><FeatureGate feature="fullStory"><FullStoryList /></FeatureGate></P>} />
        <Route path="/full-story/:id" element={<P><FeatureGate feature="fullStory"><FullStoryEditor /></FeatureGate></P>} />
        <Route path="/full-story-video" element={<P><FeatureGate feature="fullStoryVideo"><FullStoryVideoList /></FeatureGate></P>} />
        <Route path="/full-story-video/templates/plates" element={<P><FeatureGate feature="fullStoryVideo"><PlateTemplates /></FeatureGate></P>} />
        <Route path="/full-story-video/:id" element={<P><FeatureGate feature="fullStoryVideo"><FullStoryVideoEditor /></FeatureGate></P>} />

        {/* Legacy editor */}
        <Route path="/editor/:type" element={<P><ContentEditor /></P>} />

        {/* Content pages */}
        <Route path="/video-to-script" element={<P><VideoToScript /></P>} />
        <Route path="/video-to-video"  element={<P><VideoToVideo /></P>} />
        <Route path="/quick-ai" element={<P><QuickAI /></P>} />
        <Route path="/media-ai" element={<P><MediaAI /></P>} />
        <Route path="/anchor-formats" element={<P><NewsAnchorFormats /></P>} />
        <Route path="/breaking-plates" element={<P><BreakingPlates /></P>} />
        <Route path="/breaking-points" element={<P><BreakingPoints /></P>} />
        <Route path="/tops" element={<P><Tops /></P>} />
        <Route path="/news-bulletin" element={<P><NewsBulletin /></P>} />
        <Route path="/headlines-slugs" element={<P><HeadlinesSlugs /></P>} />
        <Route path="/breaking-news" element={<P><BreakingNewsPresentation /></P>} />
        <Route path="/breaking-news-presentation" element={<P><BreakingNewsPresentation /></P>} />
        <Route path="/scrollings" element={<P><Scrollings /></P>} />
        <Route path="/anchor-bg-titles" element={<P><AnchorBgTitles /></P>} />
        <Route path="/voice-over" element={<P><VoiceOver /></P>} />
        <Route path="/speed-news" element={<P><SpeedNews /></P>} />
        <Route path="/script-modes" element={<P><ScriptModes /></P>} />
        <Route path="/indepth-30" element={<P><Indepth30 /></P>} />
        <Route path="/off-the-record-30" element={<P><OffTheRecord30 /></P>} />
        <Route path="/special-focus-30" element={<P><SpecialFocus30 /></P>} />
        <Route path="/analysis-10" element={<P><Analysis10 /></P>} />
        <Route path="/indepth" element={<P><Indepth /></P>} />
        <Route path="/off-the-record" element={<P><OffTheRecord /></P>} />
        <Route path="/special-focus" element={<P><SpecialFocus /></P>} />
        <Route path="/analysis" element={<P><Analysis /></P>} />
        <Route path="/social-media" element={<P><SocialMedia /></P>} />
        <Route path="/promo-script" element={<P><PromoScript /></P>} />
        <Route path="/digital-anchor-visual" element={<P><DigitalAnchorVisual /></P>} />
        <Route path="/commercial-interviews" element={<P><CommercialInterviews /></P>} />
        <Route path="/sports" element={<P><Sports /></P>} />
        <Route path="/conspiracy-theory" element={<P><ConspiracyTheory /></P>} />
        <Route path="/burning-issue" element={<P><BurningIssue /></P>} />
        <Route path="/tcr" element={<P><TCR /></P>} />
        <Route path="/world-news" element={<P><WorldNews /></P>} />
        <Route path="/crime-story" element={<P><CrimeStory /></P>} />

        {/* Script to Voice */}
        <Route path="/script-to-voice" element={<P><FeatureGate feature="scriptToVoice"><ScriptToVoice /></FeatureGate></P>} />

        {/* Language Change (Dubbing) */}
        <Route path="/language-change" element={<P><LanguageChange /></P>} />

        {/* Admin — Voice Artist Manager */}
        <Route path="/admin/voice-artists" element={<P admin><VoiceArtistManager /></P>} />

        {/* Admin — Dubbing History (all users) */}
        <Route path="/admin/dubbing-history" element={<P admin><DubbingHistoryAdmin /></P>} />

        {/* Settings sub-routes */}
        <Route path="/settings" element={<P><Navigate to="/settings/profile" replace /></P>} />
        <Route path="/settings/profile" element={<P><ProfileSettings /></P>} />
        <Route path="/settings/ai-provider" element={<P><AIProviderSettings /></P>} />
        <Route path="/settings/user-management" element={<P admin allowManager><UserManagement /></P>} />
        <Route path="/settings/prompt-editor" element={<P admin><PromptEditor /></P>} />
        <Route path="/settings/editorial-rules" element={<P admin><EditorialRules /></P>} />
        <Route path="/settings/usage-billing" element={<P admin allowManager><UsageBilling /></P>} />
        <Route path="/settings/ip-whitelist" element={<P admin><IpWhitelistSettings /></P>} />
        <Route path="/settings/media-ai" element={<P admin><MediaAIProviders /></P>} />
        <Route path="/settings/automation-sources" element={<P admin allowManager><AutomationSources /></P>} />

        {/* Admin */}
        <Route path="/admin" element={<P admin><AdminPanel /></P>} />

        {/* Default */}
        <Route path="/" element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <ToastContainer
        theme={theme}
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Router>
  );
}

export default App;
